<template>
  <div class="card mb-25 border-0 rounded-0 bg-white">
    <div class="card-body p-15 p-sm-20 p-md-25 p-lg-30 letter-spacing">
      <div class="d-flex flex-wrap justify-content-between align-items-center mb-3 gap-2">
        <h5 class="mb-0">Auto System Classification Results</h5>
        <div class="d-flex gap-2">
          <button class="btn btn-outline-secondary" type="button" @click="refresh" :disabled="loading">
            Refresh
          </button>
          <button class="btn btn-outline-primary" type="button" @click="exportCsv" :disabled="visibleRows.length === 0">
            Export CSV
          </button>
        </div>
      </div>

      <div v-if="error" class="alert alert-danger" role="alert">{{ error }}</div>
      <div v-else-if="loading" class="text-center py-4">
        <div class="spinner-border text-primary" role="status">
          <span class="visually-hidden">Loading...</span>
        </div>
      </div>
      <div v-else>
        <div v-if="columns.length === 0" class="text-muted">No rows were returned for this table.</div>
        <div v-else>
          <div class="row g-2 mb-3">
            <div class="col-md-4 col-lg-3" v-for="column in columns" :key="column">
              <label class="form-label text-uppercase small fw-semibold">{{ formatHeader(column) }}</label>
              <input
                type="text"
                class="form-control form-control-sm shadow-none rounded-0"
                v-model="columnFilters[column]"
                :placeholder="`Filter ${formatHeader(column)}`"
              />
            </div>
          </div>

          <div class="table-responsive">
            <table class="table table-striped table-hover align-middle mb-0">
              <thead>
                <tr>
                  <th
                    v-for="column in columns"
                    :key="column"
                    class="sortable"
                    @click="toggleSort(column)"
                  >
                    <span>{{ formatHeader(column) }}</span>
                    <span v-if="sortState.column === column" class="ms-1">
                      <i v-if="sortState.direction === 'asc'" class="flaticon-up-arrow"></i>
                      <i v-else class="flaticon-down-arrow"></i>
                    </span>
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(row, rowIndex) in visibleRows" :key="rowIndex">
                  <td v-for="column in columns" :key="column">
                    {{ formatCell(row[column]) }}
                  </td>
                </tr>
                <tr v-if="visibleRows.length === 0">
                  <td :colspan="columns.length" class="text-center text-muted py-4">
                    No rows match the current filters.
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <div class="d-flex justify-content-between align-items-center mt-3 flex-wrap gap-2">
            <div class="text-muted small">
              Showing {{ visibleRows.length.toLocaleString() }} of {{ rows.length.toLocaleString() }} rows
            </div>
            <button
              type="button"
              class="btn btn-link p-0 text-decoration-none"
              @click="clearFilters"
              :disabled="!hasActiveFilters"
            >
              Clear filters
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, ref, reactive, computed, watch, onMounted } from 'vue';
import axios from 'axios';
import { BASE_HSUITE_URL } from '@/core/constant/env';

type ResultRow = Record<string, unknown>;
type SortDirection = 'asc' | 'desc';

export default defineComponent({
  name: 'AutoSystemClassificationResultsTable',
  props: {
    tableName: {
      type: String,
      required: true
    }
  },
  setup(props) {
    const rows = ref<ResultRow[]>([]);
    const loading = ref(false);
    const error = ref<string | null>(null);
    const columnFilters = reactive<Record<string, string>>({});
    const sortState = reactive<{ column: string; direction: SortDirection }>({ column: '', direction: 'asc' });

    const columns = computed(() => {
      const ordered: string[] = [];
      const seen = new Set<string>();
      rows.value.forEach((row) => {
        Object.keys(row).forEach((key) => {
          if (!seen.has(key)) {
            seen.add(key);
            ordered.push(key);
          }
        });
      });
      return ordered;
    });

    watch(columns, (newColumns) => {
      newColumns.forEach((column) => {
        if (!(column in columnFilters)) {
          columnFilters[column] = '';
        }
      });
      Object.keys(columnFilters).forEach((key) => {
        if (!newColumns.includes(key)) {
          delete columnFilters[key];
        }
      });
      if (sortState.column && !newColumns.includes(sortState.column)) {
        sortState.column = '';
      }
    });

    const hasActiveFilters = computed(() =>
      Object.values(columnFilters).some((value) => value?.trim().length)
    );

    const matchesFilter = (value: unknown, filter: string) => {
      const trimmed = filter.trim().toLowerCase();
      if (!trimmed) {
        return true;
      }
      if (value === null || value === undefined) {
        return false;
      }
      if (typeof value === 'number') {
        return value.toString().toLowerCase().includes(trimmed);
      }
      if (typeof value === 'boolean') {
        return (value ? 'true' : 'false').includes(trimmed);
      }
      return String(value).toLowerCase().includes(trimmed);
    };

    const filteredRows = computed(() => {
      if (!hasActiveFilters.value) {
        return rows.value;
      }
      return rows.value.filter((row) =>
        columns.value.every((column) => matchesFilter(row[column], columnFilters[column] ?? ''))
      );
    });

    const normaliseForSort = (value: unknown) => {
      if (value === null || value === undefined) {
        return '';
      }
      if (typeof value === 'number') {
        return value;
      }
      if (typeof value === 'boolean') {
        return value ? 1 : 0;
      }
      const asString = String(value);
      const timestamp = Date.parse(asString);
      if (!Number.isNaN(timestamp) && /^\d{4}-\d{2}-\d{2}/.test(asString)) {
        return timestamp;
      }
      return asString.toLowerCase();
    };

    const sortedRows = computed(() => {
      if (!sortState.column) {
        return filteredRows.value;
      }
      const column = sortState.column;
      const direction = sortState.direction;
      return [...filteredRows.value].sort((a, b) => {
        const left = normaliseForSort(a[column]);
        const right = normaliseForSort(b[column]);
        let comparison: number;
        if (typeof left === 'number' && typeof right === 'number') {
          comparison = left - right;
        } else {
          comparison = String(left).localeCompare(String(right), undefined, { numeric: true, sensitivity: 'base' });
        }
        return direction === 'asc' ? comparison : -comparison;
      });
    });

    const visibleRows = computed(() => sortedRows.value);

    const formatHeader = (header: string) =>
      header
        .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
        .replace(/_/g, ' ')
        .replace(/\s+/g, ' ')
        .trim()
        .replace(/^./, (char) => char.toUpperCase());

    const formatCell = (value: unknown) => {
      if (value === null || value === undefined) {
        return '�';
      }
      if (typeof value === 'number') {
        return value.toLocaleString();
      }
      if (typeof value === 'boolean') {
        return value ? 'Yes' : 'No';
      }
      if (value instanceof Date) {
        return value.toISOString();
      }
      if (Array.isArray(value) || typeof value === 'object') {
        try {
          return JSON.stringify(value);
        } catch (error) {
          return String(value);
        }
      }
      return String(value);
    };

    const csvEscape = (input: unknown) => {
      if (input === null || input === undefined) {
        return '';
      }
      const value = typeof input === 'string' ? input : formatCell(input);
      const needsEscaping = /[",\n\r]/.test(value);
      const escaped = value.replace(/"/g, '""');
      return needsEscaping ? `"${escaped}"` : escaped;
    };

    const exportCsv = () => {
      if (!columns.value.length || !visibleRows.value.length) {
        return;
      }
      const headerRow = columns.value.map((column) => csvEscape(formatHeader(column))).join(',');
      const bodyRows = visibleRows.value.map((row) =>
        columns.value.map((column) => csvEscape(row[column])).join(',')
      );
      const csvContent = [headerRow, ...bodyRows].join('\r\n');
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = `${props.tableName || 'AutoSystemClassificationResults'}.csv`;
      link.click();
      URL.revokeObjectURL(link.href);
    };

    const toggleSort = (column: string) => {
      if (sortState.column === column) {
        sortState.direction = sortState.direction === 'asc' ? 'desc' : 'asc';
      } else {
        sortState.column = column;
        sortState.direction = 'asc';
      }
    };

    const clearFilters = () => {
      Object.keys(columnFilters).forEach((key) => {
        columnFilters[key] = '';
      });
    };

    const fetchRows = async () => {
      if (!props.tableName) {
        rows.value = [];
        return;
      }
      loading.value = true;
      error.value = null;
      try {
        const encodedName = encodeURIComponent(props.tableName);
        const { data } = await axios.get(`${BASE_HSUITE_URL}/api/elsa/GetTableData/${encodedName}`);
        rows.value = Array.isArray(data) ? data : [];
      } catch (err) {
        console.error('Failed to load Auto System Classification results', err);
        error.value = 'Unable to load results. Please try again.';
        rows.value = [];
      } finally {
        loading.value = false;
      }
    };

    const refresh = () => {
      fetchRows();
    };

    watch(
      () => props.tableName,
      () => {
        sortState.column = '';
        clearFilters();
        fetchRows();
      }
    );

    onMounted(fetchRows);

    return {
      rows,
      columns,
      loading,
      error,
      columnFilters,
      sortState,
      visibleRows,
      hasActiveFilters,
      formatHeader,
      formatCell,
      toggleSort,
      exportCsv,
      clearFilters,
      refresh
    };
  }
});
</script>

<style scoped>
.sortable {
  cursor: pointer;
  user-select: none;
  white-space: nowrap;
}

.table thead th {
  font-size: 0.875rem;
}

.table tbody td {
  font-size: 0.875rem;
}

button.btn-link:disabled {
  color: #adb5bd;
}
</style>
